import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon, SaveAlt as FileIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { jsonToXlsx } from '../../../settings/themeSettings';
import { buildSageExportFileContent, downloadSageFile, downloadSageCsvFile } from '../../../utils/sageAccountingEngine';

const PreBasculeSimulationViewer = ({
    simulationNumber = 'SIM-2026-09-001',
    selectionNumber = 'SEL-2026-09-001',
    journalPeriodStr = 'FAC — Septembre 2026 (du 01/09 au 30/09/2026)',
    nombreFactures = 0,
    ecritures = [],
    controleStats = [],
    user = 'Comptable AFT'
}) => {
    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const totalDebit = ecritures.reduce((s, e) => s + Number(e.debit || 0), 0);
    const totalCredit = ecritures.reduce((s, e) => s + Number(e.credit || 0), 0);
    const isEquilibre = Math.abs(totalDebit - totalCredit) < 1;

    const handleExportExcel = () => {
        const exportData = ecritures.map(e => ({
            'Jour': e.jour,
            'N° Pièce': e.piece,
            'N° Facture': e.numeroFacture,
            'Référence': e.numeroDossier,
            'Compte Général': e.compteGeneral,
            'Compte Tiers': e.compteTiers,
            'Libellé': e.libelle,
            'Date Échéance': e.dateEcheance,
            'Débit (CFA)': e.debit,
            'Crédit (CFA)': e.credit
        }));
        jsonToXlsx(exportData, `Simulation_PreBascule_${simulationNumber}`);
    };

    const handleDownloadTxt = () => {
        const content = buildSageExportFileContent(ecritures);
        downloadSageFile(content, `Simulation_${simulationNumber}.txt`);
    };

    const handleDownloadCsv = () => {
        const content = buildSageExportFileContent(ecritures);
        downloadSageCsvFile(content, `Simulation_${simulationNumber}.csv`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box sx={{ outline: 'none' }}>
            {/* TOOLBAR */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} className="no-print" flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    Simulation SAGE — {ecritures.length} lignes d'écritures générées
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<FileIcon />}
                        onClick={handleDownloadTxt}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export SAGE (.txt)
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<FileIcon />}
                        onClick={handleDownloadCsv}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export (.csv)
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer le document
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* DOCUMENT OFFICIEL IMPRIMABLE (PAGE 12 NOTE SAGE) */}
            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1150px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM SARL
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#0f172a' }}>
                                Pré-bascule — Simulation des écritures comptables
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>

                    {/* GRILLE DES MÉTADONNÉES */}
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" display="block">
                                <strong>N° de simulation :</strong> {simulationNumber}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Journal / Période :</strong> {journalPeriodStr}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Nombre de factures :</strong> {nombreFactures}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" display="block">
                                <strong>N° de sélection d'origine :</strong> {selectionNumber}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Édité le / par :</strong> {dateTirage} — {user}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Nombre de lignes d'écriture :</strong> {ecritures.length}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* 1. TABLEAU DES ÉCRITURES SIMULÉES (FORMAT SAGE) */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Écritures simulées — Journal FAC (Présentation SAGE)
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '50px 70px 110px 120px 80px 100px 1fr 100px 110px 110px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>
                        <Box>Jour</Box>
                        <Box>N° pièce</Box>
                        <Box>N° facture</Box>
                        <Box>Référence</Box>
                        <Box>Compte</Box>
                        <Box>Compte tiers</Box>
                        <Box>Libellé</Box>
                        <Box>Échéance</Box>
                        <Box sx={{ textAlign: 'right' }}>Débit</Box>
                        <Box sx={{ textAlign: 'right' }}>Crédit</Box>
                    </Box>

                    {ecritures.map((e, idx) => (
                        <Box
                            key={e.id || idx}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 70px 110px 120px 80px 100px 1fr 100px 110px 110px',
                                p: 0.8,
                                alignItems: 'center',
                                fontSize: '0.78rem',
                                borderBottom: '1px solid #e2e8f0',
                                bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc'
                            }}
                        >
                            <Box>{e.jour}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{e.piece}</Box>
                            <Box sx={{ fontWeight: 600 }}>{e.numeroFacture}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{e.numeroDossier}</Box>
                            <Box sx={{ fontWeight: 700 }}>{e.compteGeneral}</Box>
                            <Box sx={{ color: e.compteTiers === '4111Àcréer' ? '#dc2626' : '#2563eb', fontWeight: 600 }}>
                                {e.compteTiers}
                            </Box>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.libelle}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{e.dateEcheance}</Box>
                            <Box sx={{ textAlign: 'right', fontWeight: e.debit > 0 ? 700 : 400 }}>
                                {e.debit > 0 ? formatCurrency(e.debit) : '—'}
                            </Box>
                            <Box sx={{ textAlign: 'right', fontWeight: e.credit > 0 ? 700 : 400 }}>
                                {e.credit > 0 ? formatCurrency(e.credit) : '—'}
                            </Box>
                        </Box>
                    ))}

                    {/* TOTAUX ET CONTRÔLE D'ÉQUILIBRE */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '50px 70px 110px 120px 80px 100px 1fr 100px 110px 110px',
                            bgcolor: '#f1f5f9',
                            p: 1.1,
                            fontWeight: 'bold',
                            fontSize: '0.82rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 8', textAlign: 'right', pr: 2 }}>
                            TOTAUX (FCFA) :
                        </Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalDebit)}</Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalCredit)}</Box>
                    </Box>
                </Box>

                {/* BANDEAU STATUT ÉQUILIBRE */}
                <Box sx={{ mb: 3, p: 1, bgcolor: isEquilibre ? '#dcfce7' : '#fee2e2', border: `1px solid ${isEquilibre ? '#86efac' : '#fca5a5'}`, borderRadius: '4px', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: isEquilibre ? '#166534' : '#991b1b' }}>
                        Contrôle d'équilibre (Débit = Crédit) : {isEquilibre ? 'ÉQUILIBRÉ' : 'DÉSÉQUILIBRÉ'}
                    </Typography>
                </Box>

                {/* 2. TABLEAU DES CONTRÔLES AUTOMATIQUES — DÉTECTION DES ANOMALIES */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Contrôles automatiques — Détection des anomalies
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.78rem' }}>
                        <Box>Point de contrôle</Box>
                        <Box sx={{ textAlign: 'center' }}>Nombre</Box>
                        <Box sx={{ textAlign: 'center' }}>Résultat</Box>
                    </Box>

                    {controleStats.map((c, idx) => {
                        const isOk = c.nombre === 0;
                        return (
                            <Box
                                key={c.id || idx}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 120px 140px',
                                    p: 0.9,
                                    alignItems: 'center',
                                    fontSize: '0.8rem',
                                    borderBottom: '1px solid #e2e8f0',
                                    bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc'
                                }}
                            >
                                <Box>{c.point}</Box>
                                <Box sx={{ textAlign: 'center', fontWeight: 'bold' }}>{c.nombre}</Box>
                                <Box sx={{ textAlign: 'center', fontWeight: 'bold', color: isOk ? '#16a34a' : '#dc2626' }}>
                                    {c.resultat}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* ZONE DE VISA COMPTABLE */}
                <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Contrôlé par (le comptable) :</strong> {user}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Visa / Date :</strong> _____________________________
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default PreBasculeSimulationViewer;
